import Icon from '@/components/ui/AppIcon';

interface PricingTier {
  name: string;
  description: string;
  prepaidAmount: string;
  features: string[];
  limitations: string[];
  recommended?: boolean;
}

interface PricingCardProps {
  tier: PricingTier;
}

export default function PricingCard({ tier }: PricingCardProps) {
  return (
    <div
      className={`bg-card border-2 rounded-sm p-8 relative ${
        tier.recommended ? 'border-accent shadow-institutional' : 'border-border'
      }`}
    >
      {tier.recommended && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-accent text-accent-foreground px-4 py-1 rounded-sm font-headline font-semibold text-xs">
            RECOMMENDED
          </span>
        </div>
      )}

      <div className="text-center mb-8">
        <h3 className="font-headline font-semibold text-2xl text-primary mb-2">
          {tier.name}
        </h3>
        <p className="font-body text-sm text-text-secondary mb-4">{tier.description}</p>
        <div className="flex items-baseline justify-center space-x-2">
          <span className="font-headline font-bold text-4xl text-primary">
            {tier.prepaidAmount}
          </span>
          <span className="font-body text-sm text-text-secondary">prepaid</span>
        </div>
      </div>

      <div className="space-y-6 mb-8">
        <div>
          <h4 className="font-headline font-semibold text-sm text-primary mb-3 flex items-center space-x-2">
            <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-success" />
            <span>Included Features</span>
          </h4>
          <ul className="space-y-2">
            {tier.features.map((feature, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Icon
                  name="CheckIcon"
                  size={16}
                  variant="outline"
                  className="text-success flex-shrink-0 mt-1"
                />
                <span className="font-body text-sm text-text-primary">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-headline font-semibold text-sm text-primary mb-3 flex items-center space-x-2">
            <Icon name="ExclamationTriangleIcon" size={18} variant="solid" className="text-warning" />
            <span>Service Limitations</span>
          </h4>
          <ul className="space-y-2">
            {tier.limitations.map((limitation, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Icon
                  name="MinusIcon"
                  size={16}
                  variant="outline"
                  className="text-text-secondary flex-shrink-0 mt-1"
                />
                <span className="font-body text-sm text-text-secondary">{limitation}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <button className="w-full px-6 py-3 bg-primary text-primary-foreground font-headline font-semibold text-sm rounded-sm hover:bg-secondary transition-colors duration-smooth">
        Request Service Specifications
      </button>
    </div>
  );
}